import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertTriangle,
  Shield,
  Phone,
  Mail,
  Globe,
  Heart,
  CloudRain,
  Users,
  MapPin,
  Bell,
  Plus,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface Company {
  id: string;
  name: string;
}

interface SOSAlert {
  id: string;
  country_name: string;
  alert_type: string;
  severity: string;
  title: string;
  description: string | null;
  source: string;
  valid_from: string;
  created_at: string;
}

interface AtRiskEmployee {
  id: string;
  name: string;
  destination: string;
  riskLevel: string;
  endDate: string;
}

interface EmergencyContact {
  id: string;
  contact_type: string;
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
}

interface SOSCenterTabProps {
  company: Company | null;
}

const SOSCenterTab = ({ company }: SOSCenterTabProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<SOSAlert[]>([]);
  const [atRiskEmployees, setAtRiskEmployees] = useState<AtRiskEmployee[]>([]);
  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>([]);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    contact_type: 'internal',
    phone: '',
    email: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, [company?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchAlerts(),
        fetchAtRiskEmployees(),
        fetchEmergencyContacts(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    const { data, error } = await supabase
      .from('sos_alerts')
      .select('*')
      .eq('is_active', true)
      .order('severity', { ascending: false })
      .order('created_at', { ascending: false });

    if (!error && data) {
      setAlerts(data);
    }
  };

  const fetchAtRiskEmployees = async () => {
    if (!company?.id) return;

    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('remote_work_requests')
      .select(`
        id,
        destination_country,
        end_date,
        risk_level,
        employees!inner(first_name, last_name)
      `)
      .eq('company_id', company.id)
      .eq('hr_approval_status', 'approved')
      .eq('manager_approval_status', 'approved')
      .gte('end_date', today)
      .in('risk_level', ['medium', 'high']);

    if (!error && data) {
      setAtRiskEmployees(data.map((req: any) => ({
        id: req.id,
        name: `${req.employees?.first_name || ''} ${req.employees?.last_name || ''}`.trim(),
        destination: req.destination_country || 'Unknown',
        riskLevel: req.risk_level || 'medium',
        endDate: req.end_date,
      })));
    }
  };

  const fetchEmergencyContacts = async () => {
    if (!company?.id) return;

    const { data, error } = await supabase
      .from('company_emergency_contacts')
      .select('*')
      .eq('company_id', company.id)
      .eq('is_active', true)
      .order('sort_order');

    if (!error && data) {
      setEmergencyContacts(data);
    }
  };

  const handleAddContact = async () => {
    if (!company?.id || !newContact.name) return;

    const { error } = await supabase
      .from('company_emergency_contacts')
      .insert({
        company_id: company.id,
        ...newContact,
      });

    if (error) {
      toast({
        variant: "destructive",
        title: t("common.error", "Error"),
        description: t("dashboard.sosCenter.contactAddError", "Failed to add contact"),
      });
    } else {
      toast({
        title: t("common.success", "Success"),
        description: t("dashboard.sosCenter.contactAdded", "Contact added successfully"),
      });
      setNewContact({ name: '', contact_type: 'internal', phone: '', email: '', notes: '' });
      setIsAddContactOpen(false);
      fetchEmergencyContacts();
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'security':
        return <Shield className="h-4 w-4" />;
      case 'health':
        return <Heart className="h-4 w-4" />;
      case 'weather':
        return <CloudRain className="h-4 w-4" />;
      case 'political':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Globe className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-600 text-white';
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  const getContactTypeIcon = (type: string) => {
    switch (type) {
      case 'insurance':
        return <Shield className="h-4 w-4" />;
      case 'embassy':
        return <Globe className="h-4 w-4" />;
      case 'medical':
        return <Heart className="h-4 w-4" />;
      default:
        return <Phone className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            {t("dashboard.sosCenter.title", "SOS Center")}
          </h2>
          <p className="text-muted-foreground">
            {t("dashboard.sosCenter.description", "Duty of care and emergency management")}
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {t("dashboard.sosCenter.activeAlerts", "Active Alerts")}
            </CardTitle>
            <CardDescription>
              {alerts.length} {t("dashboard.sosCenter.alertsActive", "active alerts worldwide")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Shield className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{t("dashboard.sosCenter.noAlerts", "No active alerts")}</p>
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${getSeverityColor(alert.severity)}`}>
                        {getAlertIcon(alert.alert_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{alert.title}</h4>
                          <Badge variant="outline" className="text-xs">
                            {alert.country_name}
                          </Badge>
                        </div>
                        {alert.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {alert.description}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(alert.created_at).toLocaleDateString()} • {alert.source}
                        </p>
                      </div>
                    </div>
                    <Badge className={getSeverityColor(alert.severity)}>
                      {alert.severity}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* At-Risk Employees */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t("dashboard.sosCenter.atRiskEmployees", "At-Risk Employees")}
            </CardTitle>
            <CardDescription>
              {atRiskEmployees.length} {t("dashboard.sosCenter.employeesInRiskAreas", "employees in elevated risk areas")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
            {atRiskEmployees.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Shield className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{t("dashboard.sosCenter.noAtRisk", "No employees in elevated risk areas")}</p>
              </div>
            ) : (
              atRiskEmployees.map((employee) => (
                <div
                  key={employee.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <span className="text-sm font-medium">
                        {employee.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{employee.name}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {employee.destination}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      className={
                        employee.riskLevel === 'high'
                          ? 'bg-red-100 text-red-700 border-red-200'
                          : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                      }
                    >
                      {employee.riskLevel}
                    </Badge>
                    <Button variant="outline" size="sm">
                      {t("dashboard.sosCenter.sendAlert", "Alert")}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Emergency Contacts */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                {t("dashboard.sosCenter.emergencyContacts", "Emergency Contacts")}
              </CardTitle>
              <CardDescription>
                {t("dashboard.sosCenter.emergencyContactsDesc", "Quick access to emergency support")}
              </CardDescription>
            </div>
            <Dialog open={isAddContactOpen} onOpenChange={setIsAddContactOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  {t("common.add", "Add")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("dashboard.sosCenter.addContact", "Add Emergency Contact")}</DialogTitle>
                  <DialogDescription>
                    {t("dashboard.sosCenter.addContactDesc", "Add a new emergency contact for your company")}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>{t("dashboard.sosCenter.contactName", "Name")}</Label>
                    <Input
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      placeholder="e.g., HR Emergency Line"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("dashboard.sosCenter.contactType", "Type")}</Label>
                    <Select
                      value={newContact.contact_type}
                      onValueChange={(v) => setNewContact({ ...newContact, contact_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">Internal</SelectItem>
                        <SelectItem value="insurance">Insurance</SelectItem>
                        <SelectItem value="embassy">Embassy</SelectItem>
                        <SelectItem value="medical">Medical</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t("dashboard.sosCenter.phone", "Phone")}</Label>
                      <Input
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                        placeholder="+33 1 23 45 67 89"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("dashboard.sosCenter.email", "Email")}</Label>
                      <Input
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                        placeholder="emergency@company.com"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("dashboard.sosCenter.notes", "Notes")}</Label>
                    <Input
                      value={newContact.notes}
                      onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                      placeholder="Available 24/7"
                    />
                  </div>
                  <Button onClick={handleAddContact} className="w-full">
                    {t("dashboard.sosCenter.saveContact", "Save Contact")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {emergencyContacts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Phone className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{t("dashboard.sosCenter.noContacts", "No emergency contacts configured")}</p>
                <p className="text-sm">{t("dashboard.sosCenter.addContactPrompt", "Add your first contact to get started")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {emergencyContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary">
                        {getContactTypeIcon(contact.contact_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate">{contact.name}</h4>
                        <Badge variant="outline" className="text-xs mt-1">
                          {contact.contact_type}
                        </Badge>
                        {contact.phone && (
                          <p className="text-sm mt-2 flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contact.phone}
                          </p>
                        )}
                        {contact.email && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </p>
                        )}
                        {contact.notes && (
                          <p className="text-xs text-muted-foreground mt-2 italic">
                            {contact.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SOSCenterTab;
